import React from "react";
import { Box } from "@mui/material";
import { Transition } from "react-transition-group";
import { motion, tokens } from "../theme";

/**
 * The toast, as the book draws it in the Motion patterns panel: page-background
 * ground, a hairline border, 8px radius, and a 7px status dot ringed in its own
 * tint. No icon glyph and no saturated fill — a toast is the same family as an
 * inline message, said somewhere else.
 *
 * Motion is "240ms decelerate, 8px rise". Decelerate is the book's curve for
 * something arriving; the rise is deliberately short, because a toast that
 * travels far reads as an animation rather than as a thing that appeared.
 */

const TONES = {
  success: { dot: tokens.successText, halo: tokens.successTint },
  warning: { dot: tokens.warningText, halo: tokens.warningTint },
  error: { dot: tokens.danger, halo: tokens.dangerTint },
  info: { dot: tokens.infoText, halo: tokens.infoTint },
};

const RISE = 8;
const DURATION = motion.duration[3];

// The whole shorthand is rewritten per state rather than overriding
// `transitionTimingFunction` on top of it. React warns when a shorthand and one
// of its longhands are both set and one is later removed, and the removal wins
// in a way that is hard to see.
const ease = (curve) =>
  `opacity ${DURATION}ms ${curve}, transform ${DURATION}ms ${curve}`;

// Leaving uses accelerate, not decelerate: the book pairs "something arriving"
// with decelerate and "something leaving the screen for good" with accelerate,
// and a dismissed toast is not coming back.
const arriving = { transition: ease(motion.easing.decelerate) };
const leaving = {
  opacity: 0,
  transform: `translateY(${RISE}px)`,
  transition: ease(motion.easing.accelerate),
};

const transitionStyles = {
  entering: { ...arriving, opacity: 1, transform: "translateY(0)" },
  entered: { ...arriving, opacity: 1, transform: "translateY(0)" },
  exiting: leaving,
  exited: leaving,
};

/**
 * Snackbar hands its transition `in`, `onEnter`, `onExited` and friends, and
 * expects the child to hold the ref. `Transition` takes the callbacks by the
 * same names, so the props spread straight through.
 */
const ToastTransition = React.forwardRef(function ToastTransition(
  { children, in: inProp, onEnter, onExited, ...rest },
  ref
) {
  const nodeRef = React.useRef(null);
  return (
    <Transition
      nodeRef={nodeRef}
      in={inProp}
      timeout={DURATION}
      appear
      onEnter={() => onEnter && onEnter(nodeRef.current, false)}
      onExited={() => onExited && onExited(nodeRef.current, false)}
      {...rest}
    >
      {(state) =>
        React.cloneElement(children, {
          ref: (node) => {
            nodeRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
          },
          style: {
            opacity: 0,
            transform: `translateY(${RISE}px)`,
            transition: ease(motion.easing.decelerate),
            ...transitionStyles[state],
            ...children.props.style,
          },
        })
      }
    </Transition>
  );
});

const Toast = React.forwardRef(function Toast({ severity = "info", message, style }, ref) {
  const tone = TONES[severity] || TONES.info;
  return (
    <Box
      ref={ref}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: "8px",
        padding: "12px 16px",
        fontSize: "0.8125rem",
        color: tokens.text,
        boxShadow: "0 4px 12px -4px rgba(42,29,34,0.16)",
        maxWidth: "24rem",
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          background: tone.dot,
          boxShadow: `0 0 0 4px ${tone.halo}`,
          flex: "none",
        }}
      />
      <Box>{message}</Box>
    </Box>
  );
});

export { ToastTransition };
export default Toast;
